import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import parentApi from "@/lib/parentApi";
import { toast } from "sonner";
import { SignaturePad } from "@/components/SignaturePad";

const fmt = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Parent: incident reports for a child — read + e-sign the ones awaiting signature. */
export function IncidentSignCard({ studentId }) {
  const [items, setItems] = useState([]);
  const [sig, setSig] = useState({});
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => parentApi.get(`/parent/students/${studentId}/incidents`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items.length) return null;

  const sign = async (inc) => {
    if (!sig[inc.id] || !name.trim()) return;
    setBusy(inc.id);
    try { await parentApi.post(`/parent/incidents/${inc.id}/sign`, { signature_data_url: sig[inc.id], signer_name: name.trim(), parent_comment: comment }); toast.success("Report signed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not sign"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="incident-sign-card">
      {items.map((i) => (
        <div key={i.id} className={`rounded-2xl border p-4 ${i.status === "reviewed" ? "border-red-200 bg-red-50/50" : "border-slate-200 bg-white"}`} data-testid={`parent-incident-${i.id}`}>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><AlertTriangle className={`h-4 w-4 ${i.status === "reviewed" ? "text-red-600" : "text-slate-400"}`} /> Incident report · <span className="capitalize">{i.type.replace("_", " ")}</span><span className="ml-auto text-xs font-normal text-slate-500">{fmt(i.occurred_at)}</span></div>
          <p className="mt-2 text-sm text-slate-800">{i.description}</p>
          {i.first_aid && <p className="mt-1 text-sm text-slate-600"><b>First aid:</b> {i.first_aid}</p>}
          {i.admin_note && <p className="mt-2 rounded-lg bg-white px-3 py-2 text-xs text-slate-600"><b>Director:</b> {i.admin_note}</p>}
          {i.status === "signed" ? (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Signed by {i.signer_name} · {fmt(i.signed_at)}</div>
          ) : (
            <div className="mt-3 space-y-2">
              <div className="text-xs font-medium text-slate-700">Please sign to acknowledge you've read this report.</div>
              <SignaturePad onChange={(d) => setSig((s) => ({ ...s, [i.id]: d }))} testid={`incident-sig-${i.id}`} />
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" data-testid={`incident-signer-${i.id}`} className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary" />
              <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comment for the center (optional)" className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary" />
              <button onClick={() => sign(i)} disabled={busy === i.id || !sig[i.id] || !name.trim()} data-testid={`incident-sign-btn-${i.id}`} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy === i.id && <Loader2 className="h-4 w-4 animate-spin" />} Sign report</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
